import type { KeyDerivationConfiguration, KeyDerivationParameters } from '../../application/models';
import { keyDerivationIsWithinBounds } from '../../domain';
import { base64ToBytes, bytesToBase64 } from './base64';
import { createBrowserArgon2idWorker } from './browser-worker-client';
import type { Argon2idWorkerFactory } from './argon2id-worker.protocol';

/**
 * RFC 9106's second recommended profile for memory-constrained environments.
 * ADR-006 keeps every cost configurable so a target device can calibrate toward 0.5–1 s.
 */
export const DEFAULT_ARGON2ID_CONFIGURATION: KeyDerivationConfiguration = Object.freeze({
  algorithm: 'Argon2id',
  memoryCostKiB: 65_536,
  iterations: 3,
  parallelism: 1,
  keyLengthBytes: 32,
  saltLengthBytes: 16,
});

export interface DerivedKeyMaterial {
  readonly bytes: Uint8Array;
  readonly parameters: KeyDerivationParameters;
}

export class Argon2idKdfError extends Error {
  constructor() {
    super('Argon2id key derivation failed');
    this.name = 'Argon2idKdfError';
  }
}

const hasStoredSalt = (parameters: unknown): parameters is KeyDerivationParameters =>
  typeof parameters === 'object' && parameters !== null && 'salt' in parameters;

const isKeyDerivationConfiguration = (
  parameters: unknown,
): parameters is KeyDerivationConfiguration =>
  typeof parameters === 'object' && parameters !== null;

export class Argon2idKdf {
  constructor(
    private readonly random: Pick<Crypto, 'getRandomValues'> = globalThis.crypto,
    private readonly workerFactory: Argon2idWorkerFactory = createBrowserArgon2idWorker,
  ) {}

  deriveKey(passphrase: string, configuration: unknown): Promise<DerivedKeyMaterial> {
    if (!hasStoredSalt(configuration) && !isKeyDerivationConfiguration(configuration)) {
      return Promise.reject(new Argon2idKdfError());
    }
    // Defence in depth: whatever let these settings through, no derivation runs out of bounds.
    if (!keyDerivationIsWithinBounds(configuration as KeyDerivationConfiguration)) {
      return Promise.reject(new Argon2idKdfError());
    }
    const parameters = hasStoredSalt(configuration)
      ? configuration
      : this.createParameters(configuration);
    const worker = this.workerFactory();

    return new Promise<DerivedKeyMaterial>((resolve, reject) => {
      const fail = (): void => {
        worker.terminate();
        reject(new Argon2idKdfError());
      };

      worker.onMessage((response) => {
        if (!response.ok) {
          fail();
          return;
        }
        worker.terminate();
        resolve(Object.freeze({ bytes: response.keyBytes, parameters }));
      });
      worker.onError(fail);

      try {
        worker.post({ passphrase, parameters });
      } catch {
        fail();
      }
    });
  }

  private createParameters(configuration: KeyDerivationConfiguration): KeyDerivationParameters {
    const salt = new Uint8Array(configuration.saltLengthBytes);
    this.random.getRandomValues(salt);
    return Object.freeze({ ...configuration, salt: bytesToBase64(salt) });
  }

  static decodeSalt(parameters: KeyDerivationParameters): Uint8Array {
    return base64ToBytes(parameters.salt);
  }
}
